import React, { Fragment, useEffect, useState, SyntheticEvent, Dispatch, SetStateAction } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import managementService from "../../../services/AdminManagementService";
import MuiAlert from '@mui/material/Alert';
import { SiteDTO, DefaultPasswordDTO } from "@beable/roster-backend-client-v1";
import { getStyles } from "../styles";
import { validatePassword } from "@beable/mwm-commons-password-validation";
import backgroundDataUri from "./pdfBackground";
import pdfMake from "./pdfMakeClient";
import { Margins } from "pdfmake/interfaces";
import capitalize from "lodash/capitalize";

interface PasswordManagementProps {
  site: SiteDTO;
  showMessage: (msg: string, severity: "error" | "info" | "success" | "warning") => void;
  closeMessage: () => void;
  messageSettings: {
    open: boolean;
    message: string;
    severity: 'error' | 'info' | 'success' | 'warning';
  };
  setSite: Dispatch<SetStateAction<SiteDTO>>;
}
const emptyDefaultPassword: DefaultPasswordDTO = {
  administrator: "",
  teacher: "",
  student: "",
};

export const PasswordManagement = ({
  site,
  showMessage,
  closeMessage,
  messageSettings,
  setSite
}: PasswordManagementProps) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  const [allowPasswordChange, setAllowPasswordChange] = useState<boolean>(
    site.allowPasswordChange || false
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [defaultPasswords, setDefaultPasswords] =
    useState<DefaultPasswordDTO>(emptyDefaultPassword);
  const [downloadLoginInstructions, setDownloadLoginInstructions] =
    useState<boolean>(false);
  const [extend30DayWindow, setExtend30DayWindow] =
    useState<boolean>(false);
  const [disablePasswordDisplay, setDisablePasswordDisplay] =
    useState<boolean>(false);
  const [passwordErrors, setPasswordErrors] =
    useState<DefaultPasswordDTO>(emptyDefaultPassword);
  const [showPasswordRules, setShowPasswordRules] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(true);

  const errorMessage = "Invalid password. See rules above.";
  const passwordRules =
    "Passwords need to be more than 8 characters and include at least one number, letter and symbol.";

  useEffect(() => {
    setDownloadLoginInstructions(false);
    setAllowPasswordChange(site.allowPasswordChange ?? false);
    setPasswordErrors(emptyDefaultPassword);
    setDefaultPasswords(emptyDefaultPassword);
    setShowPasswordRules(false);
    if (site.allowPasswordChange) {
      setDisablePasswordDisplay(true);
      return;
    }
    setDisablePasswordDisplay(false);
  }, [site]);

  const setDefaultPasswordOptions =
    (name: keyof DefaultPasswordDTO) =>
      (
        e: React.ChangeEvent<{
          name?: string | undefined;
          value: unknown;
        }>
      ) => {
        const password = e.target.value as string;
        const validationResponse = validatePassword(password);
        setDefaultPasswords({
          ...defaultPasswords,
          [name]: password,
        });
        if (validationResponse.isErr()) {
          setPasswordErrors({
            ...passwordErrors,
            [name]: "error",
          });
          return;
        }

        setPasswordErrors({
          ...passwordErrors,
          [name]: "",
        });
      };

  useEffect(() => {
    if (
      isLoading ||
      (site.allowPasswordChange === allowPasswordChange &&
        !downloadLoginInstructions && !extend30DayWindow) ||
      (allowPasswordChange &&
        !disablePasswordDisplay &&
        (!defaultPasswords.administrator ||
          !defaultPasswords.teacher ||
          !defaultPasswords.student ||
          passwordErrors.administrator === 'error' ||
          passwordErrors.teacher === 'error' ||
          passwordErrors.student === 'error'))
    ) {
      setDisabled(true);
      return;
    }
    setDisabled(false);
  }, [
    allowPasswordChange,
    defaultPasswords,
    disablePasswordDisplay,
    downloadLoginInstructions,
    extend30DayWindow,
    isLoading,
    passwordErrors,
    site.allowPasswordChange,
  ]);

  const handlePasswordControlChange = async (e: any) => {
    setDownloadLoginInstructions(false);
    setAllowPasswordChange(!allowPasswordChange);
    if (e.target.value) {
      setShowPasswordRules(true);
      return;
    }
    setShowPasswordRules(false);
  };

  const submitPasswordControlChange = async (e: any) => {
    if (site && site.allowPasswordChange !== allowPasswordChange) {
      setIsLoading(true);
      const updateResp = await managementService.updatePasswordChangeSettings(
        site.id,
        allowPasswordChange,
        defaultPasswords
      );
      if (updateResp) {
        extendWindow();
        showMessage("Password Control changed successfully!", "success");
        downloadInstructions();
        setSite({ ...site, allowPasswordChange: allowPasswordChange });
      } else {
        showMessage("Could not save password control...", "error");
      }
      setIsLoading(false);
      return;
    }
    downloadInstructions();
    extendWindow();
  };

  const downloadInstructions = async () => {
    if (downloadLoginInstructions) {
      const response = await managementService.getDefaultPasswords(site.id);
      if (response) {
        exportToPdf(response);
      }
    }
  };

  const extendWindow = async () => {
    if (extend30DayWindow) {
      const response = await managementService.extendLoginWindow(site.id);
      if (response) {
        showMessage("Password extended for 30 days.", "success");
        return;
      }
      showMessage("Oops. Something went wrong. Try again later.", "error");
    }
  };

  const processPDFContent = (passwords: DefaultPasswordDTO) => {
    const content: any[] = [];
    const keys = Object.keys(passwords);

    keys.forEach((key) => {
      const type = key as keyof DefaultPasswordDTO;
      content.push({
        text: `Welcome to Beable, ${capitalize(key)}s!`,
        style: "header",
        pageBreak: "before",
      });
      content.push({ text: "\n" });
      content.push({
        text: "Here’s how to login! ",
        style: "subheader",
      });
      content.push({ text: "\n" });
      content.push({
        text: "Follow these simple instructions to login to Beable for the first time.",
        style: "text",
      });
      content.push({
        ul: [
          { text: `Go here: ${site.subdomain}.login.beable.com`, style: "list", },
          {
            text: "Enter your username: \n ____________________________________________",
            style: "list",
          },
          {
            text: `Enter your temp password: ${passwords[type]}`,
            style: "list",
          },
          {
            text: "Follow the directions to set up a new password that you can remember.",
            style: "list",
          },
        ], margin: [40, 0, 0, 0]
      });
    });
    // now add spanish page for students only
    content.push({
      text: `Estudiantes, ¡bienvenidos a Beable!`,
      style: "header",
      pageBreak: "before",
    });
    content.push({ text: "\n" });
    content.push({
      text: "Aquí les indicamos cómo iniciar sesión",
      style: "subheader",
    });
    content.push({ text: "\n" });
    content.push({
      text: "Sigan estas sencillas instrucciones la primera vez que ingresen a Beable.",
      style: "text",
    });
    content.push({
      ul: [
        { text: `Vayan a: ${site.subdomain}.login.beable.com`, style: "list", },
        {
          text: "Ingresen su nombre de usuario: \n ____________________________________________",
          style: "list",
        },
        {
          text: `Ingresen su contraseña provisional: ${passwords.student}`,
          style: "list",
        },
        {
          text: "Sigan las instrucciones para crear una nueva contraseña que puedan recordar fácilmente.",
          style: "list",
        },
      ], margin: [40, 0, 0, 0]
    });

    return content;
  };

  const exportToPdf = (passwords: DefaultPasswordDTO) => {
    const fileName = "BeableLoginInstructions";

    const content = processPDFContent(passwords);

    const docDefinition = {
      info: {
        title: fileName,
        author: "Beable",
      },
      pageMargins: [75, 150, 60, 225] as Margins,
      background: {
        image: `data:image/png;base64, ${backgroundDataUri()}`,
        height: 840,
        width: 590,
      },
      content: [
        "\n",
        "\n",
        {
          text: "Welcome to Beable!",
          style: "header",
        },
        "\n",
        {
          text: "Instructions for distributing",
          style: "subheader",
        },
        "\n",
        "\n",
        {
          text: `The following pages can be used to tell teachers, staff, and students how to login to Beable for the first time. Be sure to write in guidance for how your users will know what to use for their username. For example, you might write: “Use your staff ID” or “Use your student ID”, or “Your username is the same as your Portal ID”. Once you’ve written that in, you can scan, pdf or copy the pages to your new Beable users.`,
          style: "text",
        },
        "\n",
        {
          text: `If you have any questions, please contact Beable support at support@beable.com or 833-866-8066.`,
          style: "text",
        },
        "\n",
        content,
      ],
      styles: {
        header: {
          fontSize: 27,
          bold: true,
          color: "#00aced",
        },
        subheader: {
          fontSize: 22,
          bold: true,
          color: "#e3155e",
        },
        text: {
          fontSize: 14,
          lineHeight: 1.25,
        },
        list: {
          fontSize: 14,
          lineHeight: 1.5,
        },
      },
    };

    const pdf = pdfMake.createPdf(docDefinition);

    try {
      pdf.download(`${fileName}.pdf`);
    } catch (e) {
      alert('Error encountered while attempting to download passwords. Please try again!');
    }
  };

  const PasswordFields = () => {
    return (
      <Fragment>
        <Box sx={{ width: "95%" }}>
          <TextField
            name="adminInitialPassword"
            variant="outlined"
            label="Admin Initial Password "
            onBlur={setDefaultPasswordOptions("administrator")}
            disabled={disablePasswordDisplay}
            fullWidth
            defaultValue={
              disablePasswordDisplay ? "*****" : defaultPasswords.administrator
            }
            sx={styles.indentedFields}
            error={passwordErrors.administrator === "error"}
            helperText={
              passwordErrors.administrator === "error" ? errorMessage : ""
            }
            slotProps={{
              htmlInput: {
                "data-testid": "adminInitialPasswordInput",
              }
            }}
          />
          <TextField
            name="teacherInitialPassword"
            variant="outlined"
            label="Teacher Initial Password"
            onBlur={setDefaultPasswordOptions("teacher")}
            disabled={disablePasswordDisplay}
            fullWidth
            defaultValue={
              disablePasswordDisplay ? "*****" : defaultPasswords.teacher
            }
            sx={styles.indentedFields}
            error={passwordErrors.teacher === "error"}
            helperText={passwordErrors.teacher === "error" ? errorMessage : ""}
            slotProps={{
              htmlInput: {
                "data-testid": "teacherInitialPasswordInput",
              }
            }}
          />
          <TextField
            name="studentInitialPassword"
            variant="outlined"
            label="Student Initial Password"
            onBlur={setDefaultPasswordOptions("student")}
            disabled={disablePasswordDisplay}
            fullWidth
            defaultValue={
              disablePasswordDisplay ? "*****" : defaultPasswords.student
            }
            sx={styles.indentedFields}
            error={passwordErrors.student === "error"}
            helperText={passwordErrors.student === "error" ? errorMessage : ""}
            slotProps={{
              htmlInput: {
                "data-testid": "studentInitialPasswordInput",
              }
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="downloadLoginInstructions"
                checked={downloadLoginInstructions}
                onChange={() => {
                  setDownloadLoginInstructions(!downloadLoginInstructions);
                }}
              />
            }
            label="Download first time login instructions"
            sx={styles.cbs}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="extend30DayWindow"
                checked={extend30DayWindow}
                onChange={() => {
                  setExtend30DayWindow(!extend30DayWindow);
                }}
              />
            }
            label="Extend 30-Day window"
            sx={styles.cbs}
          />
        </Box>
      </Fragment>
    );
  };

  return (
    <>
      <TextField
        id="passwordControlSelect"
        select
        label="Select Password Control"
        value={allowPasswordChange ? 1 : 0}
        onChange={handlePasswordControlChange}
        data-testid="passwordControlSelector"
        helperText={showPasswordRules ? passwordRules : ""}
        sx={styles.pFormControl}
        fullWidth
        variant="outlined"
      >
        <MenuItem value={1} role="option">
          Beable Managed
        </MenuItem>
        <MenuItem value={0} role="option">
          District Provided and Managed
        </MenuItem>
      </TextField>
      {allowPasswordChange && <PasswordFields />}
      <Box sx={styles.rightAlign}>
        <Button
          variant="contained"
          color="primary"
          id="savePasswordControl"
          onClick={submitPasswordControlChange}
          disabled={disabled}
        >
          Save
        </Button>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={messageSettings.open}
        autoHideDuration={5000}
        onClose={closeMessage}
      >
        <MuiAlert
          onClose={closeMessage}
          severity={messageSettings.severity}
          elevation={6}
          variant="filled"
        >
          {messageSettings.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};
